import { useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { requestPasswordReset } from "../api/auth";
import { getApiError } from "../api/errors";
import AppLogo from "../components/AppLogo";
import LanguageSelector from "../components/LanguageSelector";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [identifier, setIdentifier] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!identifier.trim()) return;

    setSubmitting(true);
    setError("");

    try {
      await requestPasswordReset(identifier.trim());
      setSubmitted(true);
    } catch (requestError) {
      setError(getApiError(requestError, t("auth.resetRequestError")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: { xs: 1.5, sm: 3 },
        background:
          "radial-gradient(circle at 8% 12%, rgba(56,189,248,0.2), transparent 28%), radial-gradient(circle at 92% 15%, rgba(124,58,237,0.2), transparent 30%), #020617",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 520,
          borderRadius: 5,
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 36px 100px rgba(0,0,0,.42)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}><LanguageSelector /></Box>
          <AppLogo />

          {submitted ? (
            <Stack spacing={2.25} alignItems="center" textAlign="center" sx={{ mt: 5 }}>
              <MarkEmailReadRoundedIcon color="primary" sx={{ fontSize: 62 }} />
              <Typography variant="h4">{t("auth.checkEmail")}</Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {t("auth.checkEmailBody")}
              </Typography>
              <Button component={Link} to="/login" variant="contained" fullWidth sx={{ mt: 1, py: 1.15 }}>
                {t("auth.returnSignIn")}
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setSubmitted(false);
                  setError("");
                }}
              >
                {t("auth.differentIdentifier")}
              </Button>
            </Stack>
          ) : (
            <>
              <Typography variant="h4" sx={{ mt: 5 }}>
                {t("auth.forgotTitle")}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75, mb: 3, lineHeight: 1.7 }}>
                {t("auth.forgotBody")}
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  required
                  autoFocus
                  type="text"
                  autoComplete="username"
                  label={t("auth.identifier")}
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  inputProps={{ maxLength: 254 }}
                  autoCapitalize="none"
                  spellCheck={false}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{ mt: 2, py: 1.15 }}
                >
                  {submitting ? <CircularProgress size={23} color="inherit" /> : t("auth.sendReset")}
                </Button>
              </Box>

              <Button
                component={Link}
                to="/login"
                startIcon={<ArrowBackRoundedIcon />}
                sx={{ mt: 2.5, px: 0 }}
              >
                {t("auth.backSignIn")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
